import * as React from 'react';
import { useEffect } from "react";
import { Chart } from 'chart.js';
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import { axesConfig, setTooltipColorScheme, hexToRgb, chartConfigPlugin } from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { getText } from "../../../translations";

const LineChart = ({
    title,
    data,
    siteVariables,
    gradients,
    collapsed,
    tabKey,
    theme,
}: {
    title: string;
    data: IChartData;
    siteVariables: SiteVariablesPrepared;
    gradients?: boolean;
    collapsed?: boolean;
    tabKey?: number;
    theme?: string;
}) => {
    const { colorScheme, t } = siteVariables;
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const chartRef = React.useRef<Chart | undefined>();
    const chartId = React.useMemo(
        () => Math.random().toString(36).substr(2, 9),
        []
    );
    const chartDataPointColors = React.useMemo(
        () => [
            '#27438C',
            '#508BD5',
            '#ED1B23',
            '#D8D8D8',
        ],
        [theme]
    );

    const createAreaChartDataPoints = (
        ctx: CanvasRenderingContext2D
    ): Chart.ChartDataSets[] =>
        Array.from(data.datasets, (set, i) => {
            const gradientStroke = ctx.createLinearGradient(0, 0, 0, gradients ? ctx.canvas.clientHeight * 0.8 : 0);
            const hoverGradientStroke = ctx.createLinearGradient(0, 0, 0, gradients ? ctx.canvas.clientHeight * 0.8 : 0);
            const colorRGB = hexToRgb(chartDataPointColors[i]);
            gradientStroke.addColorStop(0, `rgba(${colorRGB}, .0)`);
            gradientStroke.addColorStop(1, `rgb(80, 139, 213, .5)`);
            hoverGradientStroke.addColorStop(0, `rgba(${colorRGB}, .6)`);
            hoverGradientStroke.addColorStop(1, `rgb(80, 139, 213, .0)`);

            let dataPointConfig = {
                label: getText(t?.locale, set.label),
                data: set.data,
                borderColor: chartDataPointColors[i],
                hoverBorderColor: chartDataPointColors[i],
                hoverBorderWidth: 2,
                backgroundColor: gradientStroke as any,
                hoverBackgroundColor: hoverGradientStroke as any,
                borderWidth: 2,
                pointBorderColor: chartDataPointColors[i],
                pointBackgroundColor: chartDataPointColors[i],
                pointHoverBackgroundColor: chartDataPointColors[i],
                pointHoverBorderColor: chartDataPointColors[i],
                pointHoverBorderWidth: 0,
                borderCapStyle: "round",
                borderJoinStyle: "round",
                pointBorderWidth: 0,
                pointRadius: 0,
                pointHoverRadius: 0,
                pointStyle: "circle",
                borderDash: set.borderDash,
            };

            return dataPointConfig as Chart.ChartDataSets;
        });

    useEffect(() => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;

        const config: any = chartConfigPlugin(tabKey!, theme);

        config.options.tooltips.intersect = false;
        config.options.scales.yAxes[0].ticks.beginAtZero = true;

        config.options.tooltips.bodyFontSize = 9;

        config.options.tooltips.callbacks.title = (tooltipItems: any) => {
            return `${Intl.NumberFormat('pt-BR').format(tooltipItems[0].yLabel)} - ${tooltipItems[0].xLabel}` || 0 + ` - ${tooltipItems[0].xLabel}`;
        };

        config.options.responsive = true;

        config.options.tooltips.callbacks.footer = (tooltipItems: any) => null;

        chartRef.current = new Chart(ctx, {
            ...(config as any),
            data: {
                labels: Array.isArray(data.labels)
                    ? data.labels.map((label) => getText(t?.locale, label))
                    : getText(t?.locale, data.labels),
                datasets: data.datasets,
            },
            variables: siteVariables
        });

        return () => {
            if (!chartRef.current) return;
            if (canvasRef.current) {

            }
            chartRef.current.destroy();
        };
    }, [data]);

    /**
     * Theme updates
     */
    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // Apply new colors scheme for data points
        chartRef.current.data.datasets = createAreaChartDataPoints(ctx)
        // Update tooltip colors scheme
        setTooltipColorScheme({
            chart: chartRef.current,
            siteVariables,
            chartDataPointColors,
        });

        // Update axeses
        axesConfig({ chart: chartRef.current, ctx, colorScheme });
        // Show style changes
        chartRef.current.update();
    }, [theme, data]);

    function onLegendClick(datasetIndex: number) {
        if (!chartRef.current) return;
        chartRef.current.data.datasets![datasetIndex].hidden = !chartRef.current
            .data.datasets![datasetIndex].hidden;
        chartRef.current.update();
    }

    return (
        <ChartContainer
            siteVariables={siteVariables}
            data={data}
            chartDataPointColors={chartDataPointColors}
            onLegendClick={onLegendClick}
            hideLegend={collapsed}
        >
            <canvas
                id={chartId}
                ref={canvasRef}
                tabIndex={0}
                style={{
                    userSelect: "none",
                }}
                aria-label={title}
            >
                {data.datasets.map((set, setKey) =>
                    (set.data as number[]).forEach((item: number, itemKey: number) => (
                        // Generated tooltips for screen readers
                        <div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
                            <p>{item}</p>
                            <span>
                                {getText(t?.locale, set.label)}: {set.data[itemKey]}
                            </span>
                        </div>
                    ))
                )}
            </canvas>
        </ChartContainer>
    );
};

export const numberFormat = (value: number | string): string =>
    String(value)
        .split("")
        .reverse()
        .join("")
        .replace(/(\d{3})/g, "$1.")
        .replace(/\,$/, "")
        .split("")
        .reverse()
        .join("");

export default LineChart;